import React, { useState, useEffect } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * BurgerMenu Component
 * Sidebar navigation menu that appears on the left side
 *
 * Features:
 * - Accessible keyboard navigation (Escape to close)
 * - ARIA labels for screen readers
 * - Focus management
 * - Responsive mobile navigation
 */
const BurgerMenu = ({ tabs, activeTab, setActiveTab }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});

  // Handle keyboard navigation (Escape key to close)
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      // Prevent body scroll when menu is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  const handleTabClick = (index) => {
    setActiveTab(index);
    setIsOpen(false); // Close menu after selection
  };

  const toggleCategory = (categoryLabel) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryLabel]: !prev[categoryLabel]
    }));
  };

  return (
    <>
      {/* Burger Menu Button */}
      <button
        className="burger-menu-btn"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={isOpen ? 'Close navigation menu' : 'Open navigation menu'}
        aria-expanded={isOpen}
        aria-controls="burger-menu-sidebar"
      >
        {isOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Overlay when menu is open */}
      {isOpen && (
        <div
          className="burger-menu-overlay"
          onClick={() => setIsOpen(false)}
          aria-hidden="true"
          role="presentation"
        />
      )}

      {/* Sidebar Menu */}
      <nav
        id="burger-menu-sidebar"
        className={`burger-menu-sidebar ${isOpen ? 'open' : ''}`}
        role="navigation"
        aria-label="Main navigation"
      >
        <div className="burger-menu-header">
          <h3 className="mb-0">{t('navigationMenu')}</h3>
          <button
            className="burger-menu-close"
            onClick={() => setIsOpen(false)}
            aria-label="Close menu"
            aria-expanded={isOpen}
          >
            <X size={24} />
          </button>
        </div>

        <ul className="burger-menu-list" role="menubar">
          {tabs.map((tab, index) => {
            if (tab.category) {
              // Render category with submenu
              const isExpanded = expandedCategories[tab.label];
              return (
                <li key={index} role="none" className="burger-menu-category">
                  <button
                    className="burger-menu-category-btn"
                    onClick={() => toggleCategory(tab.label)}
                    role="menuitem"
                    aria-expanded={isExpanded}
                    aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${tab.label} section`}
                  >
                    <span>{tab.label}</span>
                    <ChevronDown
                      size={18}
                      className={`chevron ${isExpanded ? 'expanded' : ''}`}
                    />
                  </button>

                  {/* Submenu */}
                  {isExpanded && (
                    <ul className="burger-submenu" role="menu">
                      {tab.submenu && tab.submenu.map((submenu, subIndex) => (
                        <li key={`${index}-${subIndex}`} role="none">
                          <button
                            className={`burger-menu-item submenu-item ${activeTab === submenu.index ? 'active' : ''}`}
                            onClick={() => handleTabClick(submenu.index)}
                            role="menuitem"
                            aria-current={activeTab === submenu.index ? 'page' : undefined}
                            aria-label={`Navigate to ${submenu.label} section`}
                          >
                            {submenu.label}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            } else {
              // Render regular menu item
              return (
                <li key={index} role="none">
                  <button
                    className={`burger-menu-item ${activeTab === tab.index ? 'active' : ''}`}
                    onClick={() => handleTabClick(tab.index)}
                    role="menuitem"
                    aria-current={activeTab === tab.index ? 'page' : undefined}
                    aria-label={`Navigate to ${tab.label} section`}
                  >
                    {tab.label}
                  </button>
                </li>
              );
            }
          })}
        </ul>
      </nav>
    </>
  );
};

export default BurgerMenu;
